import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { timeSlots, SchedulingData } from "@/lib/validation";

interface SchedulingStepProps {
  data: SchedulingData;
  onDataChange: (data: SchedulingData) => void;
  errors?: Record<string, string[]>;
}

const SchedulingStep = ({
  data,
  onDataChange,
  errors,
}: SchedulingStepProps) => {
  // Disable past dates and Sundays
  const disabledDays = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-2">
          Schedule Your Free Estimate
        </h2>
        <p className="text-muted-foreground">
          Choose a convenient time for our expert to visit your home.
        </p>
      </div>

      {/* Date Picker */}
      <div className="space-y-2">
        <Label className="text-base font-medium">Preferred Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full h-12 justify-start text-left font-normal text-base",
                !data.preferredDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {data.preferredDate ? (
                format(data.preferredDate, "EEEE, MMMM d, yyyy")
              ) : (
                <span>Select a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={data.preferredDate}
              onSelect={(date) =>
                onDataChange({ ...data, preferredDate: date as Date })
              }
              disabled={disabledDays}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        {errors?.preferredDate && (
          <p className="text-destructive text-sm">{errors.preferredDate[0]}</p>
        )}
      </div>

      {/* Time Slots */}
      <div className="space-y-3">
        <Label className="text-base font-medium">Preferred Time</Label>
        <RadioGroup
          value={data.preferredTime}
          onValueChange={(value) =>
            onDataChange({
              ...data,
              preferredTime: value as typeof data.preferredTime,
            })
          }
          className="grid sm:grid-cols-3 gap-3"
        >
          {timeSlots.map((slot) => (
            <motion.div key={slot} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <label
                className={cn(
                  "flex items-center justify-center gap-2 p-4 rounded-lg border cursor-pointer transition-all",
                  data.preferredTime === slot
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
              >
                <RadioGroupItem value={slot} className="sr-only" />
                <span className="text-sm text-foreground text-center">{slot}</span>
              </label>
            </motion.div>
          ))}
        </RadioGroup>
        {errors?.preferredTime && (
          <p className="text-destructive text-sm">{errors.preferredTime[0]}</p>
        )}
      </div>

      {/* Additional Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes" className="text-base font-medium">
          Additional Notes (Optional)
        </Label>
        <Textarea
          id="notes"
          placeholder="Tell us anything else we should know about your project..."
          value={data.additionalNotes || ""}
          onChange={(e) =>
            onDataChange({ ...data, additionalNotes: e.target.value })
          }
          className="min-h-[120px] text-base resize-none"
        />
        {errors?.additionalNotes && (
          <p className="text-destructive text-sm">{errors.additionalNotes[0]}</p>
        )}
      </div>
    </motion.div>
  );
};

export default SchedulingStep;
